import type { ErrorClasses, Field, ParsedError, Settings, _UnparsedError } from "./types";

export function handleErrors(errors: _UnparsedError[], settings: Settings) {
  settings.internal.parsedErrors = errors.map((error) => parseError(error));

  const customErrors = settings.events.error(settings.internal.parsedErrors);

  if (customErrors) {
    settings.internal.parsedErrors = customErrors;
  }

  settings.internal.parsedErrors.forEach((error) => {
    showError(error, settings);
  });
}

export function parseError(error: _UnparsedError): ParsedError {
  if (error.errorMessage) {
    if (error.errorMessage.includes("Violation of PRIMARY KEY")) {
      error.errorCode = 10;
      error.errorMessage = "Email address already exists";
    }

    if (error.errorMessage.includes("Cannot insert the value")) {
      const match = error.errorMessage.match(/column '(.+?)'/);

      if (match) {
        const name = match[1];
        error.errorCode = 11;
        error.errorMessage = `Provide a value for ${name}`;
      }
    }

    if (error.errorMessage.includes("CustomerKey")) {
      error.errorCode = 12;
      error.errorMessage = `Invalid data extension`;
    }

    if (error.errorMessage.includes("Errors occurred")) {
      error.errorCode = 13;
      error.errorMessage = "Something went wrong";
    }
  }

  return {
    field: error.field,
    code: error.errorCode,
    message: error.errorMessage,
    handleError: error.handleError
  };
}

function addErrorListItem(errorListItem: HTMLLIElement, settings: Settings) {
  if (!settings.errorListEl) {
    const errorListEl = document.createElement("ul");
    errorListEl.role = "alert";
    errorListEl.classList.add(settings.errorListClass);

    settings.errorListEl = errorListEl;

    if (settings.errorPosition === "bottom") {
      settings.errorEl.append(errorListEl);
    } else {
      settings.errorEl.prepend(errorListEl);
    }
  }

  settings.errorListEl.append(errorListItem);
}

export function addFieldErrorListItem(errorListItem: HTMLLIElement, field: Field, settings: Settings) {
  if (!field.errorEl || settings.errorPosition === "top" || settings.errorPosition === "bottom") {
    field.errorEl = document.createElement("ul");
    field.errorEl.classList.add(settings.errorFieldListClass);
  }

  if (!field.errorEls) {
    field.errorEls = [];
  }

  field.errorEls.push(errorListItem);

  if (field.errorEl) {
    field.errorEl.append(errorListItem);
  }
}

export function showError(error: ParsedError, settings: Settings) {
  const field = error.field as Field;

  const errorListItem = document.createElement("li");
  errorListItem.classList.add(settings.errorClass);
  errorListItem.innerHTML = error.message;

  if (!field || settings.errorPosition === "top" || settings.errorPosition === "bottom") {
    if (field) {
      addFieldErrorListItem(errorListItem, field, settings);
      field.classList.add(settings.errorFieldClass);
    }

    addErrorListItem(errorListItem, settings);
  } else {
    if (error.handleError) {
      const classes: ErrorClasses = {
        errorClass: settings.errorClass,
        errorFieldClass: settings.errorFieldClass,
        errorFieldListClass: settings.errorFieldListClass,
        errorListClass: settings.errorListClass
      };

      addFieldErrorListItem(errorListItem, field, settings);
      error.handleError(field, error.message, field.errorEl, error.code, classes);
    } else if (settings.errorPosition === "before" || settings.errorPosition === "after") {
      addFieldErrorListItem(errorListItem, field, settings);

      field.classList.add(settings.errorFieldClass);
      field[settings.errorPosition](field.errorEl);
    }
  }
}

export function removeErrorList(settings: Settings) {
  if (settings.errorListEl) {
    settings.errorListEl.remove();
    delete settings.errorListEl;
  }
}

export function removeFieldError(field: Field, settings: Settings) {
  if (field.errorEl) {
    field.errorEl.remove();
    delete field.errorEl;

    field.classList.remove(settings.errorFieldClass);
  }

  if (field.errorEls && field.errorEls.length > 0) {
    field.errorEls.forEach((errorEl) => {
      errorEl.remove();
    });

    delete field.errorEls;
  }

  if (settings.errorListEl && settings.errorListEl.childNodes.length === 0) {
    settings.errorListEl.remove();
    delete settings.errorListEl;
  }

  field.classList.remove(settings.errorFieldClass);
}

export function removeAllErrors(fields: Field[], settings: Settings) {
  removeErrorList(settings);

  fields.forEach((field) => {
    removeFieldError(field, settings);
  });

  settings.internal.errors = [];
  settings.internal.parsedErrors = [];

  settings.events.errorsRemoved();
}
